// client/src/pages/secure/MyOnboardingPage.js — the new licensee's or employee's own checklist.
//
// What they still owe, in their own words: documents to sign (the links arrive by email)
// and documents to upload (a licence copy, and whatever else the office asks for). An
// upload here files into their record and closes the step; the office sees it at once.

import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';

const fmt = (d) => (d ? new Date(d).toLocaleDateString() : null);

const MyOnboardingPage = () => {
    const api = useApiClient();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        try { setData(await api.get('/api/onboarding/mine')); setError(''); }
        catch (e) { setError(e.message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const upload = async (step, file) => {
        if (!file) return;
        setBusy(step.onboarding_step_id); setError(''); setNotice('');
        try {
            const fd = new FormData();
            fd.append('documentFile', file);
            const r = await api.post(`/api/onboarding/mine/steps/${step.onboarding_step_id}/upload`, fd, true);
            setNotice(r.message); load();
        } catch (e) { setError(e.message); }
        finally { setBusy(null); }
    };

    if (!data) return <div className="p-8">{error || 'Loading…'}</div>;
    const active = data.onboardings.filter((o) => o.status === 'in_progress');

    return (
        <div className="p-4 md:p-8 space-y-6 max-w-4xl">
            <div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-800">My Onboarding</h1>
                <p className="text-slate-600 text-sm">What you still need to do before you are fully set up. Documents to sign arrive by email with their own signing links; documents to upload go here.</p>
            </div>
            {notice && <div className="p-3 rounded bg-green-50 text-green-800 text-sm">{notice}</div>}
            {error && <div className="p-3 rounded bg-red-50 text-red-700 text-sm">{error}</div>}
            {!data.onboardings.length && <p className="text-slate-500">You have no onboarding in progress.</p>}

            {data.onboardings.map((o) => {
                const left = o.steps.filter((s) => s.status === 'pending').length;
                return (
                    <div key={o.onboarding_id} className="bg-white rounded-lg shadow p-4 md:p-6">
                        <div className="flex flex-wrap justify-between gap-2 mb-3">
                            <h2 className="text-lg font-semibold">{o.name}</h2>
                            <span className={`text-sm ${o.status === 'completed' ? 'text-emerald-700' : 'text-slate-600'}`}>
                                {o.status === 'completed' ? `Complete${o.completed_at ? ` ${fmt(o.completed_at)}` : ''}` : `${left} step(s) left`}
                            </span>
                        </div>
                        {o.assigned_email && <p className="text-sm text-slate-600 mb-3">Your brokerage email: <strong>{o.assigned_email}</strong></p>}
                        <ul className="divide-y">
                            {o.steps.map((s) => (
                                <li key={s.onboarding_step_id} className="py-3 flex flex-wrap justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className={`font-medium ${s.status !== 'pending' ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{s.title}</div>
                                        {s.status === 'pending' && s.due_on && <div className="text-xs text-slate-500">Due {fmt(s.due_on)}</div>}
                                        {s.status === 'pending' && s.form_instance_id && <div className="text-xs text-indigo-700">Check your email for the signing link{s.form_status === 'partially_signed' ? ' — you have signed; waiting on the office' : ''}.</div>}
                                        {s.status === 'pending' && !s.yours && <div className="text-xs text-slate-500">The office completes this.</div>}
                                    </div>
                                    <div>
                                        {s.status === 'pending' && s.automation === 'upload_document' ? (
                                            <label className={`px-3 py-2 text-sm rounded cursor-pointer ${busy === s.onboarding_step_id ? 'bg-indigo-300 text-white' : 'bg-slate-800 text-white hover:bg-slate-900'}`}>
                                                {busy === s.onboarding_step_id ? 'Uploading…' : `Upload ${s.document_type || 'file'}`}
                                                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.heic" disabled={busy === s.onboarding_step_id} onChange={(e) => upload(s, e.target.files[0])} />
                                            </label>
                                        ) : s.status !== 'pending' ? <span className="text-sm text-emerald-700">Done</span> : null}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            })}
            {active.length === 0 && data.onboardings.length > 0 && <p className="text-sm text-slate-500">Everything is done — welcome aboard.</p>}
        </div>
    );
};

export default MyOnboardingPage;
